import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { API } from '../api';
import EntityHeader, { EntityHeaderSkeleton } from '../ui/EntityHeader';
import EntityPage, { EntityLoadProblem, EntityTabs, TabTable, clearFiltersAction, useEntityTab } from '../ui/EntityPage';
import DataTable from '../ui/DataTable';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import Badge, { SeverityBadge, StatusBadge } from '../ui/Badge';
import { formatCount, formatDate, formatDateTime, plural } from '../ui/format';
import useAsync from '../ui/useAsync';
import usePagedQuery from '../ui/usePagedQuery';
import useUrlFilters from '../ui/useUrlFilters';
import ImageTicketsPanel from './ImageTicketsPanel';
import {
  IMAGE_SEVERITY_OPTIONS,
  TRIVY_STATUS_OPTIONS,
  highestImageSeverity,
  imageLocation,
  osLabel,
  scanModeLabel,
  scanOutcome,
  shortDigest,
  trivyStatusLabel,
} from './imageFields';

const TAB_IDS = ['findings', 'scans'];
const muted = (text) => <span className="text-graphite">{text}</span>;
const dateCell = (value) => <span className="text-graphite tabular-nums">{formatDate(value) || '—'}</span>;
const digestCell = (digest) => (digest ? <span className="font-mono" title={digest}>{shortDigest(digest)}</span> : muted('Unknown'));

const FINDING_COLUMNS = [
  { key: 'severity', header: 'Severity', render: (f) => <SeverityBadge severity={f.severity} /> },
  { key: 'vuln', header: 'Vulnerability', render: (f) => <EntityLink kind="cve" cve={f.vuln_id} /> },
  { key: 'package', header: 'Package', mono: true, maxWidth: 220, fullText: (f) => f.package_name, render: (f) => f.package_name },
  { key: 'installed', header: 'Installed', mono: true, maxWidth: 160, fullText: (f) => f.installed_version, render: (f) => f.installed_version },
  { key: 'fixed', header: 'Fixed in', mono: true, maxWidth: 160, fullText: (f) => f.fixed_version, render: (f) => f.fixed_version || muted('No fix') },
  { key: 'status', header: 'Trivy status', render: (f) => trivyStatusLabel(f.trivy_status) },
  { key: 'location', header: 'Location', mono: true, maxWidth: 200, fullText: (f) => `${f.location} (${f.pkg_type})`, render: (f) => f.location },
  { key: 'first', header: 'First seen', render: (f) => dateCell(f.first_seen_at) },
  {
    key: 'ticket',
    header: 'Ticket',
    render: (f) => (f.ticket_id ? (
      <Link to={`/tickets/${f.ticket_id}`} className="inline-flex items-center gap-1.5 text-signal hover:underline">
        <span className="font-mono">#{f.ticket_id}</span>
        <StatusBadge status={f.ticket_mode === 'preview' ? 'preview' : f.ticket_status} />
      </Link>
    ) : muted('—')),
  },
];

const FINDING_FILTER_DEFAULTS = { q: '', severity: '', status: '' };
const FINDING_FILTERS = [
  { key: 'q', type: 'search', label: 'Search', placeholder: 'Vulnerability, package or path', mono: true, width: 280 },
  { key: 'severity', type: 'select', label: 'Severity', options: IMAGE_SEVERITY_OPTIONS },
  { key: 'status', type: 'select', label: 'Trivy status', options: TRIVY_STATUS_OPTIONS },
];

function FindingsTab({ image, refresh }) {
  const [filters, setFilters] = useUrlFilters(FINDING_FILTER_DEFAULTS);
  const [selected, setSelected] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const key = `${image.id}|${filters.q}|${filters.severity}|${filters.status}`;
  const params = { imageId: image.id, kw: filters.q, severity: filters.severity || undefined, trivy_status: filters.status || undefined };
  const fetchPage = useCallback(
    (paging) => API.GET_IMAGE_FINDINGS({ ...params, ...paging }),
    [key], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const query = usePagedQuery(fetchPage, key);
  useEffect(() => setSelected([]), [key]);
  useEffect(() => {
    if (refresh) query.retry();
  }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <TabTable
        label={`Findings on ${image.name}`}
        filters={FINDING_FILTERS}
        values={filters}
        onFiltersChange={setFilters}
        query={query}
        filtered={Boolean(filters.q || filters.severity || filters.status)}
        columns={FINDING_COLUMNS}
        rowKey={(f) => f.finding_id}
        rowLabel={(f) => `${f.vuln_id} in ${f.package_name}`}
        selection={{ selected, onChange: setSelected }}
        selectionBar={(
          <>
            <span className="font-semibold tabular-nums">{plural(selected.length, 'finding')} selected</span>
            <span className="mx-1 h-4 border-l border-rule" aria-hidden="true" />
            <Button size="sm" variant="primary" onClick={() => setPanelOpen(true)}>
              <Plus size={14} strokeWidth={2} aria-hidden="true" />
              Create ticket
            </Button>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSelected([])}>Clear selection</Button>
          </>
        )}
        errorTitle="Findings could not be loaded"
        empty={<EmptyState title="No findings on this container image">The last scan of the current digest found no vulnerabilities.</EmptyState>}
        filteredEmpty={(
          <EmptyState title="No findings match these filters" action={clearFiltersAction(() => setFilters(FINDING_FILTER_DEFAULTS))}>
            Search matches part of the vulnerability ID, package name or file path.
          </EmptyState>
        )}
      />
      <ImageTicketsPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        images={[{ id: image.id, name: image.name }]}
        findingIds={selected}
        onCreated={({ created }) => {
          if (created > 0) {
            setSelected([]);
            query.retry();
          }
        }}
      />
    </>
  );
}

const SEVERITY_KEYS = ['Critical', 'High', 'Medium', 'Low', 'Unknown'];
const severityBreakdown = (counts) =>
  SEVERITY_KEYS.filter((k) => counts?.[k] > 0).map((k) => `${formatCount(counts[k])} ${k.toLowerCase()}`).join(', ');

const SCAN_COLUMNS = [
  { key: 'scanned', header: 'Scanned', render: (s) => <span className="tabular-nums">{formatDateTime(s.scanned_at)}</span> },
  { key: 'digest', header: 'Digest', render: (s) => digestCell(s.digest) },
  { key: 'mode', header: 'Mode', render: (s) => scanModeLabel(s.mode) },
  { key: 'tag', header: 'Tag', mono: true, render: (s) => s.tag || muted('—') },
  {
    key: 'outcome',
    header: 'Outcome',
    render: (s) => {
      const outcome = scanOutcome(s.outcome);
      return <Badge tone={outcome.tone} title={outcome.hint || undefined}>{outcome.label}</Badge>;
    },
  },
  { key: 'findings', header: 'Findings', align: 'right', render: (s) => (s.finding_count ? formatCount(s.finding_count) : muted('0')) },
  { key: 'breakdown', header: 'By severity', grow: true, minWidth: 180, render: (s) => severityBreakdown(s.severity_counts) || muted('—') },
  { key: 'trivy', header: 'Trivy', mono: true, render: (s) => s.trivy_version || muted('—') },
];

function ScansTab({ image, scans }) {
  return (
    <DataTable
      className="flex-1"
      label={`Scans of ${image.name}`}
      columns={SCAN_COLUMNS}
      rows={scans}
      rowKey={(s) => s.id}
      empty={<EmptyState title="No scans recorded">Scans of this container image appear here as the exporter ingests them.</EmptyState>}
    />
  );
}

export default function ContainerImagePage() {
  const { id } = useParams();
  const [tab, setTab] = useEntityTab(TAB_IDS);
  const [panelOpen, setPanelOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const entity = useAsync(() => API.GET_IMAGE(id), id);
  const image = entity.data?.image;
  const scans = entity.data?.scans || [];
  const crumbs = [{ label: 'Container images', to: '/container-images' }, { label: image?.name || 'Container image' }];

  if (entity.error) {
    return (
      <EntityLoadProblem
        crumbs={crumbs}
        noun="container image"
        identifier={id}
        error={entity.error}
        onRetry={entity.retry}
        listPath="/container-images"
        listLabel="Go to Container images"
      />
    );
  }

  const severity = image && highestImageSeverity(image);
  const breakdown = image && [
    image.critical_count && `${formatCount(image.critical_count)} critical`,
    image.high_count && `${formatCount(image.high_count)} high`,
  ].filter(Boolean).join(', ');
  const os = image && osLabel(image.os_family, image.os_name);

  const facts = image && [
    { label: 'Highest severity', value: severity ? <SeverityBadge severity={severity} /> : <span className="font-normal text-graphite">None known</span> },
    {
      label: 'Findings',
      value: (
        <>
          {formatCount(image.finding_count)}
          {breakdown && <span className="ml-1.5 font-normal text-graphite">({breakdown})</span>}
        </>
      ),
    },
    { label: 'Vulnerabilities', value: formatCount(image.vuln_count) },
    { label: 'Path', value: imageLocation(image), mono: true },
    { label: 'Current digest', value: image.current_digest ? <span title={image.current_digest}>{shortDigest(image.current_digest)}</span> : null, mono: true },
    { label: 'Tags', value: (image.current_tags || []).join(' ') || null, mono: true },
    {
      label: 'OS',
      value: os && (
        <span className="inline-flex items-center gap-1.5">
          {os}
          {image.os_eosl && <Badge tone="med" title="The OS release no longer gets security updates">End of life</Badge>}
        </span>
      ),
    },
    { label: 'Architecture', value: image.architecture },
    { label: 'Last scanned', value: formatDateTime(image.last_scanned_at) },
  ];

  return (
    <EntityPage>
      {!image ? (
        <EntityHeaderSkeleton crumbs={crumbs} />
      ) : (
        <EntityHeader
          crumbs={crumbs}
          identifier={image.name}
          severity={severity}
          facts={facts}
          actions={image.finding_count > 0 && (
            <Button variant="primary" onClick={() => setPanelOpen(true)}>
              <Plus size={15} strokeWidth={2} aria-hidden="true" />
              Create tickets
            </Button>
          )}
        />
      )}
      {image && (
        <EntityTabs
          label={`${image.name} details`}
          value={tab}
          onChange={setTab}
          tabs={[
            { id: 'findings', label: 'Findings', count: image.finding_count, render: () => <FindingsTab image={image} refresh={refresh} /> },
            { id: 'scans', label: 'Scans', count: scans.length, render: () => <ScansTab image={image} scans={scans} /> },
          ]}
        />
      )}
      {image && (
        <ImageTicketsPanel
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          images={[{ id: image.id, name: image.name }]}
          onCreated={({ created }) => {
            if (created > 0) setRefresh((n) => n + 1);
          }}
        />
      )}
    </EntityPage>
  );
}
